import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICategoriaEvento,
  ICreateNota,
  INota,
  IUpdateNota,
  TipoCategoria,
  TipoNota,
} from 'modelos/src';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';

import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { NotasService } from '../../../../auxiliares/servicios/http/notas.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';

// tipoDispositivo (usado por app-listado-notas) -> modulo de ICategoriaEvento
// 'Colectivo' no tiene un valor propio en TipoCategoria, se asume equivalente a 'Vehiculo'
const MODULO_POR_TIPO_DISPOSITIVO: Record<string, TipoCategoria> = {
  Activo: 'Activo',
  Vehiculo: 'Vehiculo',
  Colectivo: 'Vehiculo',
  Luminaria: 'Luminaria',
  Alarma: 'Alarma',
};

@Component({
  selector: 'app-crear-editar-nota',
  templateUrl: './crear-editar-nota.component.html',
  styleUrl: './crear-editar-nota.component.scss',
  imports: [AuxiliaresModule],
})
export class CrearEditarNotaComponent implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  helper = inject(HelperService);
  private service = inject(NotasService);
  private dialogService = inject(DialogService);
  private lista2 = inject(Lista2Service);
  dialogRef = inject<MatDialogRef<CrearEditarNotaComponent>>(MatDialogRef);

  //DEMAS
  public loading = false;
  public id?: string;
  public idEntidad?: string;
  // Cliente de la entidad asignada (no el del usuario logueado)
  public idCliente?: string;

  public tipo?: TipoNota;
  public tipoAsignado?: string;
  public form?: FormGroup;
  public title?: string;
  public editar: boolean = false;
  public nota?: INota;
  public totalCount?: number;

  //Esto es para alarmas domiciliarias
  public contacto?: boolean = false;
  public usuario?: boolean = false;

  //Categoría de evento a la que aplica esta nota/contacto
  public categoriasEvento: ICategoriaEvento[] = [];

  //Subss
  public notas$: Subscription;

  public tiposNota: TipoNota[] = ['Nota', 'Contacto'];

  private async listarCategoriasEvento(): Promise<void> {
    try {
      const data = await firstValueFrom(this.lista2.categoriaEventos({}));
      const modulo = MODULO_POR_TIPO_DISPOSITIVO[this.tipoAsignado];
      this.categoriasEvento = data.datos.filter(
        (c) => !c.modulo || c.modulo === modulo,
      );
    } catch (error) {
      console.error('Error cargando categorías de evento:', error);
      this.categoriasEvento = [];
    }
  }

  public getPermanente() {
    return this.form?.value?.permanente;
  }

  get informacion() {
    return this.form?.get('informacion') as FormGroup;
  }

  private createForm() {
    const tipoNota = this.nota?.tipo || this.data?.tipo || '';
    const contacto = this.contacto;
    const usuario = this.usuario;

    this.form = new FormGroup({
      orden: new FormControl(this.nota?.orden, [
        Validators.required,
        Validators.pattern('^[0-9]*$'), // Solo números
      ]),
      idAsignado: new FormControl(this.nota?.idAsignado || this.idEntidad),
      permanente: new FormControl(
        this.nota?.permanente === false ? false : true,
      ),
      vigenciaDesde: new FormControl(this.nota?.vigenciaDesde),
      vigenciaHasta: new FormControl(this.nota?.vigenciaHasta),
      tipo: new FormControl(tipoNota),
      idCategoriaEvento: new FormControl(this.nota?.idCategoriaEvento),
      informacion: new FormGroup({
        contacto: new FormControl(
          this.nota?.informacion?.contacto,
          contacto ? Validators.required : null,
        ),
        telefono: new FormControl(
          this.nota?.informacion?.telefono,
          contacto ? Validators.required : null,
        ),
        interno: new FormControl(this.nota?.informacion?.interno),

        email: new FormControl(this.nota?.informacion?.email),

        //Exclusivo de alarmas domiciliarias
        palabraSeguridadNormal: new FormControl(
          this.nota?.informacion?.palabraSeguridadNormal,
        ),
        palabraSeguridadEmergencia: new FormControl(
          this.nota?.informacion?.palabraSeguridadEmergencia,
        ),
        particion: new FormControl(this.nota?.informacion?.particion, [
          Validators.pattern('^[0-9]*$'),
        ]),
        numeroUsuarioAlarma: new FormControl(
          this.nota?.informacion?.numeroUsuarioAlarma,
          usuario ? Validators.required : null,
        ), //El número de usuario sólo se pide si sos usuario
        nota: new FormControl(
          this.nota?.informacion?.nota,
          tipoNota === 'Nota' ? Validators.required : null,
        ),
      }),
      inhabilitadoDesde: new FormControl(this.nota?.inhabilitadoDesde),
      inhabilitadoHasta: new FormControl(this.nota?.inhabilitadoHasta),
    });
  }

  //Estoe es para actualizar los validadores del formulario dependiendo de si se clickea en usuario o contacto
  actualizarValidadores() {
    const telefonoControl = this.form.get('informacion.telefono');
    const numeroUsuarioControl = this.form.get(
      'informacion.numeroUsuarioAlarma',
    );

    if (this.contacto) {
      telefonoControl?.setValidators(Validators.required);
    } else {
      telefonoControl?.clearValidators();
    }
    telefonoControl?.updateValueAndValidity();

    if (this.usuario) {
      numeroUsuarioControl?.setValidators(Validators.required);
    } else {
      numeroUsuarioControl?.clearValidators();
    }
    numeroUsuarioControl?.updateValueAndValidity();
  }

  //
  public async onSubmit() {
    this.loading = true;
    const res = await this.dialogService.confirm(
      `${this.title} Nota`,
      `¿Querés ${this.title.toLowerCase()} la nota?`,
    );
    if (!res) return;
    try {
      const data = this.getData();
      if (this.editar) {
        // Update
        await this.service.update(this.id, data);
        this.helper.notifSuccess('Nota actualizada');
        this.dialogRef.close(this.id);
      } else {
        // Create
        const res = await this.service.create(data);
        this.helper.notifSuccess('Nota creada');
        this.dialogRef.close(res._id);
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public getData(): ICreateNota | IUpdateNota {
    const data: ICreateNota | IUpdateNota = this.form?.value;
    // Al crear, la nota debe quedar bajo el cliente de la entidad asignada. Si
    // no se envía, el backend usa el del permiso (cliente del usuario logueado).
    if (!this.editar && this.idCliente) {
      data.idCliente = this.idCliente;
    }
    return data;
  }
  async ngOnInit() {
    this.loading = true;

    // Si viene una nota → estamos editando
    if (this.data.nota) {
      this.id = this.data?.nota?._id;
      this.editar = true;
      this.nota = this.data?.nota;
    } else {
      this.editar = false;
    }

    this.tipo = this.data?.tipo ?? '';
    this.tipoAsignado = this.data?.tipoDispositivo;
    this.idEntidad = this.data?.idEntidad;
    this.idCliente = this.data?.idCliente;

    // TITULO
    if (this.editar) {
      if (this.data.tipo === 'Nota') this.title = 'Editar Nota';
      else if (this.data.tipo === 'Contacto') this.title = 'Editar Contacto';
      else this.title = 'Editar Nota/Contacto';
    } else {
      if (this.data.tipo === 'Nota') this.title = 'Crear Nota';
      else if (this.data.tipo === 'Contacto') this.title = 'Crear Contacto';
      else this.title = 'Crear Nota/Contacto';
    }

    // =============================
    //   DETECTAR CONTACTO / USUARIO
    // =============================
    if (this.editar && this.nota?.informacion) {
      const info = this.nota.informacion;

      // CONTACTO → si tiene al menos uno de estos
      this.contacto = !!(info.telefono || info.email || info.interno);

      // USUARIO DE ALARMA → solo si tiene número de usuario
      this.usuario = !!info.numeroUsuarioAlarma;
    }

    // Crear formulario ya con los flags correctos
    this.createForm();

    // Actualizar validadores según valores detectados
    this.actualizarValidadores();

    await this.listarCategoriasEvento();

    this.loading = false;
  }
}
